// CustomerDetails.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useMutation, useQuery } from "react-query";
import { enqueueSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import CustomDate from "../../CustomForm/CustomDate";
import CustomInput from "../../CustomForm/CustomInput";
import CustomRichText from "../../CustomForm/CustomRichText";
import FileUpload from "../../CustomForm/FileUpload";
import {
  createCustomerDetails,
  getCustomerDetails,
} from "../../../api/services/customerDetails";
import { QK } from "../../../base/qk";

const CustomerDetails = () => {
  const { id: ClientId } = useParams();

  const [formValues, setFormValues] = useState({
    date_of_birth: "",
    no_of_carers: "",
    likes_to_be_known_as: "",
    door_code: "",
    access_risks: "",
    access_instructions: "",
    reason_for_admission: "",
    previous_medical_history: "",
    care_instructions: "",
  });

  const {
    data: customerDetails,
    isLoading,
    refetch,
  } = useQuery(
    [QK.customerDetails, ClientId],
    () => getCustomerDetails(ClientId),
    {
      enabled: !!ClientId,
      onSuccess: (data) => {
        if (data) {
          setFormValues({
            date_of_birth: data?.date_of_birth || "",
            no_of_carers: data?.no_of_carers || "",
            likes_to_be_known_as: data?.likes_to_be_known_as || "",
            door_code: data?.door_code || "",
            access_risks: data?.access_risks || "",
            access_instructions: data?.access_instructions || "",
            reason_for_admission: data?.reason_for_admission || "",
            previous_medical_history: data?.previous_medical_history || "",
            care_instructions: data?.care_instructions || "",
          });
        }
      },
    }
  );

  const postMutation = useMutation(createCustomerDetails, {
    onSuccess: () => {
      enqueueSnackbar("Customer details created successfully", {
        variant: "success",
      });
      refetch();
    },
    onError: () => {
      enqueueSnackbar("Failed to create customer details", {
        variant: "error",
      });
    },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRichTextChange = (field) => (value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePost = (e) => {
    e.preventDefault();
    const formData = new FormData();

    formData.append("date_of_birth", formValues.date_of_birth);
    formData.append("no_of_carers", formValues.no_of_carers);
    formData.append("likes_to_be_known_as", formValues.likes_to_be_known_as);
    formData.append("door_code", formValues.door_code);
    formData.append("access_risks", formValues.access_risks);
    formData.append("access_instructions", formValues.access_instructions);
    formData.append("reason_for_admission", formValues.reason_for_admission);
    formData.append("previous_medical_history", formValues.previous_medical_history);
    formData.append("care_instructions", formValues.care_instructions);

    const pictureInput = document.querySelector('input[name="picture"]');
    if (pictureInput && pictureInput.files && pictureInput.files.length > 0) {
      formData.append("picture", pictureInput.files[0]);
    }

    formData.append("id", customerDetails?.id || "");
    formData.append("client", ClientId);

    postMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 4, px: 2 }}>
      <Paper
        component="form"
        onSubmit={handlePost}
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "1200px",
          p: 4,
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
        }}
      >
        <Typography
          variant="h5"
          align="center"
          sx={{
            color: "primary.main",
            fontWeight: 700,
            py: 2,
            mb: 3,
          }}
        >
          Customer Details
        </Typography>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <CustomDate
              label="Date of Birth*"
              name="date_of_birth"
              value={formValues.date_of_birth}
              onChange={handleInputChange}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <CustomInput
              label="No. of Carers"
              name="no_of_carers"
              value={formValues.no_of_carers}
              onChange={handleInputChange}
              fullWidth
              type="number"
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <CustomInput
              label="Likes to be known as"
              name="likes_to_be_known_as"
              value={formValues.likes_to_be_known_as}
              onChange={handleInputChange}
              fullWidth
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <CustomInput
              label="Door Code"
              name="door_code"
              value={formValues.door_code}
              onChange={handleInputChange}
              fullWidth
            />
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ mb: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "#475467",
              }}
            >
              Access Risks
            </Typography>
          </Box>
          <CustomRichText
            value={formValues.access_risks}
            onChange={handleRichTextChange("access_risks")}
            placeholder="Write access risks..."
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ mb: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "#475467",
              }}
            >
              Access Instructions
            </Typography>
          </Box>
          <CustomRichText
            value={formValues.access_instructions}
            onChange={handleRichTextChange("access_instructions")}
            placeholder="Write access instructions..."
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ mb: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: "#475467",
                }}
              >
                Reason for Admission
              </Typography>
            </Box>
            <CustomRichText
              value={formValues.reason_for_admission}
              onChange={handleRichTextChange("reason_for_admission")}
              placeholder="Write reason for admission..."
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ mb: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: "#475467",
                }}
              >
                Previous Medical History
              </Typography>
            </Box>
            <CustomRichText
              value={formValues.previous_medical_history}
              onChange={handleRichTextChange("previous_medical_history")}
              placeholder="Write previous medical history..."
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ mb: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: "#475467",
                }}
              >
                Care Instructions
              </Typography>
            </Box>
            <CustomRichText
              value={formValues.care_instructions}
              onChange={handleRichTextChange("care_instructions")}
              placeholder="Write care instructions..."
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <FileUpload label="Picture" name="picture" fullWidth />
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="contained"
            type="submit"
            disabled={postMutation.isLoading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: "8px",
            }}
          >
            {postMutation.isLoading ? "Saving..." : "Save Customer Details"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default CustomerDetails;